import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject, signal, ChangeDetectorRef, input } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl, ReactiveFormsModule } from '@angular/forms';
import { days, reportTiming, schedule} from '@app/modules/statistics/data/share-report-data';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastrService } from 'ngx-toastr';
import {
  getCreateOrCopySuccess,
  getProject,
  getProjectError, getProjectSuccess, getProjectSuccessResponse,
  getReportError,
  getReportPassword,
  IProjectState,
  IReportState,
  ProjectGet,
  ProjectNotificationUpdate,
  ProjectUpdate,
  ProjectUpdateSuccess,
  ReportPassword, ResetProjectState
} from '@app/stores';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { Constants } from '@app/consts';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment-timezone';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { CustomTimezonePickerComponent } from '../../../_components/custom-timezone-picker/custom-timezone-picker.component';
type RecipientForm = FormGroup<{email: FormControl<string | null>}>;
type ShareReportsForm = FormGroup<{timezone: FormControl<any>; recipients: FormArray<RecipientForm>}>;

@Component({
  selector: 'app-share-report-modal',
  templateUrl: './share-report-modal.component.html',
  styleUrls: ['./share-report-modal.component.scss'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, CreatableSelectComponent, CustomTimezonePickerComponent]
})
export class ShareReportModalComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private clipboardService = inject(Clipboard);
  private toastr = inject(ToastrService);
  private projectStore = inject<Store<IProjectState>>(Store);
  private reportStore = inject<Store<IReportState>>(Store);
  constants = inject(Constants);
  modal = inject(NgbActiveModal);

  readonly projectId = input(undefined);
  readonly selectedProject = input(undefined);
  checked$ = new BehaviorSubject('false');
  checked;
  readonly showSendEmail = signal(false);
  readonly isCopyLink = signal(false);
  readonly isDownload = signal(false);
  readonly isDisabled = signal(true);
  readonly isDailySelected = signal(false);
  submitted = false;
  isDailySelected$ = new BehaviorSubject(false);
  isShareEnabled$ = new BehaviorSubject('false');
  readonly isShareEnabled = signal(false);
  isNotificationButtonClicked = false;
  readonly isNotificationSet = signal<boolean>(undefined);
  isNotificationSet$ = new BehaviorSubject(false);
  notificationStatus = '';
  shareReportsForm: ShareReportsForm;
  passwordForm: FormGroup<{password: FormControl<string | null>}>;
  recipients: FormArray<RecipientForm>;
  readonly timing = signal<string>(undefined);
  schedule: string;
  reportTimingOptions$= new BehaviorSubject(reportTiming);
  scheduleOptions$ = new BehaviorSubject<any[]>(days);
  reportTimingOptions: any;
  readonly scheduleOptions = signal<any>(undefined);
  formLenght$ = new BehaviorSubject(1);
  formLength: number;
  link: string;
  enableSharingToggleText: string;
  unsubscriber = new Subject();
  project;
  projectTimeZone: any;
  selectedProjectTimeZone: any = null;
  readonly selectedSchedule = signal<any>(undefined);
  projectSchedule: any = {};
  // Why report notifications cannot be set now: the project is outside its active dates.
  readonly reportScheduleUnavailable = signal<string>(null);

  constructor() {
    this.projectStore.dispatch(ResetProjectState({params: {error: '', success: ''}}));
  }

  ngOnInit(): void {
    this.projectStore.dispatch(ProjectGet({projectId: this.projectId()}));
    this.link = window.location.href;
    this.initiSubs();
    this.subscribeStore();
    // this.passwordForm.patchValue({password: this.project.password});
  }

  initiSubs(): void{
    this.shareReportsForm = this.formBuilder.group({
      timezone: this.formBuilder.control<any>(null),
      recipients: this.formBuilder.array([ this.createRecipient() ])
    });

    this.passwordForm = this.formBuilder.group({
      password: ['', [Validators.required]]
    });

    this.isShareEnabled$.subscribe((val)=>{
      this.isShareEnabled.set(val === 'true');
    });
    this.isNotificationSet$.subscribe((val)=>{
      this.isNotificationSet.set(val);
    });

    this.checked$.subscribe((val)=>{
      this.checked = (val === 'true');
      this.setText(this.checked);
      this.isShareEnabled$.next(val);
    });

    this.reportTimingOptions$.subscribe((val)=>{
      this.reportTimingOptions = val;
    });

    this.scheduleOptions$.subscribe((val)=>{
      this.scheduleOptions.set(val);
    });

    this.isDailySelected$.subscribe((val)=>{
      this.isDailySelected.set(val);
    });

    this.formLenght$.subscribe((val)=>{
      this.formLength = val;
    });
  }

  get shareReportsFormValue(){
    return this.shareReportsForm.value;
  }

  get f() {
    return this.shareReportsForm.controls;
  }

  get getPasswordControl() {
    return this.passwordForm.controls;
  }

  get getPasswordValue() {
    return this.passwordForm.value;
  }

  setShareReportPassword() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.passwordForm.invalid) {
      return;
    }

    const params = {
      password: this.getPasswordValue.password
    };
    this.projectStore.dispatch(ProjectUpdate({project: params, projectId: this.projectId()}));
  }

  createRecipient(): RecipientForm {
    return this.formBuilder.group({
      email: ['',[Validators.required,Validators.email]],
    });
  }

  addRecipient(): void {
    this.recipients = this.shareReportsForm.get('recipients') as FormArray<RecipientForm>;
    this.recipients.push(this.createRecipient());
  }

  removeRecipient(recipient): void {
    this.recipients = this.shareReportsForm.get('recipients') as FormArray<RecipientForm>;
    this.formLenght$.next(this.recipients.length);
    if(this.recipients.length === 1) {
      return;
    }
    this.recipients.removeAt(recipient);
  }

  get recipientsList(): FormArray<RecipientForm> {
    return this.shareReportsForm.get('recipients') as FormArray<RecipientForm>;
  }

  copyLink(): void {
    this.isCopyLink.set(true);
    this.showSendEmail.set(false);
    this.isDownload.set(false);
  }

  copyLinkToClipboard(): void {
    this.clipboardService.copy(this.link);
    this.toastr.success('Link is Copied');
  }

  download(): void {
    this.isDownload.set(true);
    this.isCopyLink.set(false);
    this.showSendEmail.set(false);
  }

  emailReportUpdate(): void {
    this.showSendEmail.set(true);
    this.isCopyLink.set(false);
    this.isDownload.set(false);
  }

  // toggleSharing(e) {
  //   const toggle = this.checked.toString();
  //   this.projectStore.dispatch(ProjectNotificationUpdate({project: {'notification_enabled': toggle}, projectId: this.projectId}));
  // }

  timingSelected(event): void {
    if (event) {
      this.isDisabled.set(false);
      this.timing.set(event?.value ? event?.value : this.timing());
      this.isDailySelected$.next(this.timing() === 'Daily');
    } else {
      this.isDisabled.set(true);
      this.isDailySelected$.next(false);
    }
    this.schedule = '';
    this.selectedSchedule.set(null);
    if (event && event.value === 'Daily') {
      this.scheduleOptions$.next(schedule.map((x, i) => ({ name: x, value: x })));
      this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
    }

    if (event && event.value === 'Weekly') {
      this.scheduleOptions$.next(days.map((x) => ({ name: x, value: x })));
    }
    if (event && event.value === 'Monthly') {
      const date = new Date();
      const numOfDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      this.scheduleOptions$.next(Array.from({length: numOfDays}, (_, i) => ({ name: `${i + 1}`, value: `${i + 1}` })));
    }
  }

  scheduleSelected(event): void {
    if (event) {
      this.schedule = event.value;
      this.selectedSchedule.set(event?.value ? event?.value : this.selectedSchedule());
    }
  }

  setNotification(): void {
    this.isNotificationButtonClicked = true;
    this.notificationStatus = 'SET';
    if(this.shareReportsForm.get('recipients').invalid) {
      this.toastr.error('Please enter valid email address', 'Error');
      return;
    }
    if (!this.timing() || !this.schedule) {
      this.toastr.error('Please fill the timing and schedule fields', 'Error');
      return;
    }
    const recipients = this.shareReportsForm.get('recipients').value.map((val) => {
      if (val.email) {
        return val.email;
      } else {
        return;
      }
    });

    if (recipients.length < 1) {
      this.toastr.error('Please add recipients', 'Error');
      return;
    }

    let mSchedule;
    if (this.timing() === 'Weekly') {
      mSchedule = days.indexOf(this.schedule);
      this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
    }
    if (this.timing() === 'Monthly') {
      mSchedule = parseInt(this.schedule, 10);
      this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
    }
    if (this.timing() === 'Daily') {
      mSchedule = parseInt(this.schedule.substring(0, 2), 10);
      if (!this.shareReportsFormValue.timezone) {
        this.toastr.error('Please select timezone', 'Error');
        return;
      }
    }
    const settings = {
      report_timing: this.timing(),
      report_schedule: mSchedule,
      timezone: this.shareReportsForm.get('timezone').value?.nameValue || this.selectedProjectTimeZone,
      set_notification: true,
      recipients
    };
    this.projectStore.dispatch(ResetProjectState({params: {error: '', success: ''}}));
    this.projectStore.dispatch(ProjectNotificationUpdate({project: {notification_setting: settings}, projectId: this.projectId()}));
  }

  /**
   * The server refuses a schedule outside the project's active dates (activation dates, else the
   * project dates), and the report job switches one off there. Read the way the server reads
   * them: a date is midnight UTC, so a project counts as ended from its end date on.
   */
  private reportScheduleClosedReason(project): string | null {
    const start = project?.activation_starts_at || project?.start_date;
    const end = project?.activation_ends_at || project?.end_date;
    const now = moment();
    const day = (value) => moment.utc(value, 'YYYY-MM-DD');
    const rule = 'Report notifications can only be set while the project is active.';
    if (end && day(end).isValid() && day(end).isBefore(now)) {
      return `${rule} It ended on ${day(end).format('MMM D, YYYY')}.`;
    }
    if (start && day(start).isValid() && day(start).isAfter(now)) {
      return `${rule} It starts on ${day(start).format('MMM D, YYYY')}.`;
    }
    return null;
  }

  stopNotification(): void {
    this.isNotificationButtonClicked = true;
    this.notificationStatus = 'STOP';
    // const settings = this.projectSchedule ? Object.assign(this.projectSchedule) : {};
    const {recipients, report_timing, timezone, report_schedule } = this.projectSchedule;
    const settings = {
      recipients,
      report_timing,
      timezone,
      report_schedule,
      set_notification: false
    };
    this.projectStore.dispatch(ResetProjectState({params: {error: '', success: ''}}));
    this.projectStore.dispatch(ProjectNotificationUpdate({project: {notification_setting: settings}, projectId: this.projectId()}));
    // this.shareReportsForm.reset();
    // this.timing = null;
    // this.isDisabled.set(true);
    // this.selectedSchedule = null;
  }

  setText(isChecked): void {
    this.enableSharingToggleText = !isChecked? 'Enable Sharing' : 'Disable Sharing';
  }

  subscribeStore(): void {
    this.projectStore.pipe(select(getProjectError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    // this.projectStore.pipe(select(getProjectSuccess))
    //   .pipe(takeUntil(this.unsubscriber))
    //   .subscribe(success => {
    //     if (success && !this.isNotificationSet && this.isNotificationButtonClicked) {
    //       this.toastr.success('Report Notification Schedule Set Successfully!');
    //       this.isNotificationButtonClicked = false;
    //     }
    //     if (success && this.isNotificationSet && this.isNotificationButtonClicked) {
    //       this.toastr.success('Report Notification Schedule Stop Successfully!');
    //       this.isNotificationButtonClicked = false;
    //     }
    //   });

    this.projectStore.pipe(select(getProject))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(project => {
        if (project) {
          this.project = project;
          this.reportScheduleUnavailable.set(this.reportScheduleClosedReason(project));
          this.projectSchedule =(typeof this.project.notification_setting === 'string') ? JSON.parse(this.project.notification_setting) : this.project.notification_setting;
          if (this.projectSchedule) {
            if (this.projectSchedule.hasOwnProperty('report_timing')) {
              this.timing.set(this.projectSchedule.report_timing);
              this.isDisabled.set(false);
            }
          }
          this.passwordForm.patchValue({password: this.project.password});
          this.checked$.next(project?.notification_enabled);
          if(!project?.notification_enabled) {
            this.showSendEmail.set(false);
            this.isCopyLink.set(false);
            this.isDownload.set(false);
          }
          if (this.project?.timezone) {
            this.projectTimeZone = this.project?.timezone;
            this.selectedProjectTimeZone = this.projectTimeZone;
          }
          if(this.project?.notification_setting){
            this.setFormFieldValues(this.projectSchedule?.timezone, this.projectSchedule?.recipients, this.projectSchedule?.report_timing, this.projectSchedule?.report_schedule);
            this.isNotificationSet$.next(this.projectSchedule?.set_notification);
          } else{
            this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
          }
          // The password and schedule forms were patched and the recipients rebuilt.
          this.cdr.markForCheck();
        }

      });

    this.projectStore.pipe(select(getCreateOrCopySuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          const toggleState = this.checked === true ? 'true' : 'false';
          this.checked$.next(toggleState);
          if (this.notificationStatus === 'SET') {
            this.isNotificationSet$.next(true);
          }
          if (this.notificationStatus === 'STOP' || this.notificationStatus === '') {
            this.isNotificationSet$.next(false);
          }
        }
      });

    this.projectStore.pipe(select(getProjectSuccessResponse))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(updateProject => {
        if (updateProject && (this.notificationStatus === 'SET' || this.notificationStatus === 'STOP')) {
          this.project = updateProject;
          this.projectSchedule = this.project.notification_setting;
          if (this.project?.notification_setting){
            this.setFormFieldValues(this.projectSchedule?.timezone, this.projectSchedule?.recipients, this.projectSchedule?.report_timing, this.projectSchedule?.report_schedule);
            this.isNotificationSet$.next(this.project?.notification_setting.set_notification);
          } else {
            this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
          }
          if (updateProject && this.isNotificationSet() && this.isNotificationButtonClicked) {
            this.toastr.success('Report Notification Schedule Set Successfully!');
            this.isNotificationButtonClicked = false;
          }
          if (updateProject && !this.isNotificationSet() && this.isNotificationButtonClicked) {
            this.toastr.success('Report Notification Schedule Stop Successfully!');
            this.isNotificationButtonClicked = false;
          }
          // The schedule form was patched and the recipients rebuilt.
          this.cdr.markForCheck();
        }
      });

    this.reportStore.pipe(select(getReportError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastr.error(error, 'Error');
        }
      });

    // this.projectStore.pipe(select(ProjectUpdateSuccess))
    // .pipe(takeUntil(this.unsubscriber))
    // .subscribe(res => {
    //   if (res && this.submitted) {
    //   }
    // });
  }

  ngOnDestroy() {
    this.submitted = false;
  }

  setFormFieldValues(timezone, recipients, timing, scheduleIndex) {
    if (!this.projectSchedule) {
      this.shareReportsForm.reset();
      this.timing.set(null);
      this.selectedSchedule.set(null);
      return;
    }
    this.timing.set(timing);
    if (timing) {
      this.isDisabled.set(false);
      if (timing === 'Daily') {
        const mapped = schedule.map((x) => ({ name: x, value: x }));
        this.scheduleOptions$.next(mapped);
        scheduleIndex = parseInt(scheduleIndex.substring(0, 2), 10);
        this.schedule = schedule[scheduleIndex];
        this.isDailySelected$.next(true);
        if (!timezone) {
          this.shareReportsForm.patchValue({timezone: this.selectedProjectTimeZone});
        } else {
          this.shareReportsForm.patchValue({timezone});
        }
        this.selectedSchedule.set(this.scheduleOptions()[scheduleIndex]);
      }

      if (timing === 'Weekly') {
        const mapped = days.map((x) => ({ name: x, value: x }));
        this.scheduleOptions$.next(mapped);
        this.schedule = days[scheduleIndex];
        this.selectedSchedule.set(this.scheduleOptions()[scheduleIndex]);
      }
      if (timing === 'Monthly') {
        const date = new Date();
        const numOfDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
        const mapped = Array.from({length: numOfDays}, (_, i) => ({ name: `${i + 1}`, value: `${i + 1}` }));
        this.scheduleOptions$.next(mapped);
        this.schedule = scheduleIndex;
        this.selectedSchedule.set(this.scheduleOptions()[scheduleIndex - 1]);
      }
    }
    const recipientsArrayOfObjects: {email: string}[] = recipients.map((val) => ({email: val}));
    recipientsArrayOfObjects.forEach(element => {
      this.createRecipient();
    });

    const recipient = this.shareReportsForm.get('recipients') as FormArray<RecipientForm>;
    // empty form array
    while (recipient.length) {
      recipient.removeAt(0);
    }
    // A group patched with an array: no control matches, as before typed forms.
    this.shareReportsForm.patchValue(recipientsArrayOfObjects as any);
    // add form array values in a loop
    recipientsArrayOfObjects.forEach(staff => recipient.push(this.formBuilder.group(staff)));
  }

  getTimezoneValue(event) {
    this.shareReportsForm.patchValue({timezone: event});
  }
}
